import React, { useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickBasketCheckCard from "@/features/pathpick/features/basket/components/sync/PathPickBasketCheckCard";
import {
  PathPickMarketSyncKind,
  PathPickProductKey,
} from "@/features/pathpick/types";
import { PathPickBasketSelection } from "@/features/pathpick/features/basket/types";
import { getRequestableSelections } from "@/features/pathpick/features/basket/utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // 선택 상태 전체를 받는다 — 접수될 상품 수가 고른 마켓에 따라 달라지므로(마켓별 연동 상태로
  // 재신청 대상을 가린다) 개수만 받으면 모달 안에서 다시 계산할 수 없다.
  selectedProducts: Map<PathPickProductKey, PathPickBasketSelection>;
  // 신청할 수 있는 마켓 — 둘 이상이면(네이버 외 마켓 권한이 있는 바이어) 마켓을 골라 신청하고,
  // ["NAVER_SYNC"]뿐이면 선택 없이 네이버 연동만 접수한다.
  syncKinds: PathPickMarketSyncKind[];
  // 네이버 기본 정보·제휴 연결이 끝났는가 — 아니면 네이버를 처음엔 빼 두고 "기본 정보 설정 필요"를 붙인다.
  // 그래도 고를 수는 있다: 고르고 진행하면 기본 정보 설정 모달이 먼저 뜬다.
  isNaverReady: boolean;
  // 기본 정보 모달에서 취소하고 돌아왔을 때 앞서 고른 마켓을 되살린다.
  initialKinds?: PathPickMarketSyncKind[];
  isLoading: boolean;
  // 접수할 요청 종류(고른 마켓)를 넘긴다.
  onConfirm: (requestTypes: PathPickMarketSyncKind[]) => void;
}

const SYNC_CHANNEL_LABEL_KEYS: Record<PathPickMarketSyncKind, string> = {
  NAVER_SYNC: "path_pick:basket.sync_confirm.channel_naver",
  TRENBE_SYNC: "path_pick:basket.sync_confirm.channel_trenbe",
  MUSTIT_SYNC: "path_pick:basket.sync_confirm.channel_mustit",
  CAFE24_SYNC: "path_pick:basket.sync_confirm.channel_cafe24",
};

// 선택 상품을 스마트스토어 상품 등록 API(SYNC)로 연동하기 전 최종 확인 모달
function PathPickBasketSyncConfirmModal({
  isOpen,
  onClose,
  selectedProducts,
  syncKinds,
  isNaverReady,
  initialKinds,
  isLoading,
  onConfirm,
}: Props) {
  const { t } = useI18nHelper();
  const isChannelSelectable = syncKinds.length > 1;
  // 모달은 열 때마다 새로 마운트되므로 매번 처음 선택으로 시작한다 — 준비된 마켓은 모두 체크하고,
  // 네이버는 기본 정보가 끝났을 때만 체크한다(네이버 없이 다른 마켓만 연동하는 바이어가 있다).
  const [selectedKinds, setSelectedKinds] = useState<PathPickMarketSyncKind[]>(
    () =>
      initialKinds ??
      (isChannelSelectable
        ? syncKinds.filter((kind) => kind !== "NAVER_SYNC" || isNaverReady)
        : syncKinds)
  );

  // 표시 순서는 syncKinds 순서로 고정한다 — 토글 순서대로 쌓인 selectedKinds를 그대로 보내지 않는다.
  const toggleKind = (kind: PathPickMarketSyncKind) =>
    setSelectedKinds((previous) =>
      syncKinds.filter((item) =>
        item === kind ? !previous.includes(item) : previous.includes(item)
      )
    );

  // 사용량 차감 안내가 이 수치를 말하므로 선택 개수가 아니라 실제로 접수될 상품 수를 센다 —
  // 고른 마켓 중 하나라도 연동 가능한(미신청·실패·연동 해제) 상품이다.
  const selectedCount = selectedProducts.size;
  const targetCount = getRequestableSelections(
    selectedProducts,
    selectedKinds
  ).length;
  // 고른 마켓에 이미 연동됐거나 처리 중이어서 요청에서 빠지는 상품 수.
  // 마켓을 하나도 안 골랐을 때는 "제외"가 아니라 신청 자체가 없으므로 안내하지 않는다.
  const excludedCount =
    selectedKinds.length > 0 ? selectedCount - targetCount : 0;

  // 마켓 선택이 있을 때만 막는다 — 마켓을 모두 해제했거나, 고른 마켓에 이미 모두 연동돼 접수할 상품이 없으면
  // 신청할 수 없다. (네이버 단독 신청은 "이미 연동된 상품" 관문이 0개를 미리 걸러 이 모달까지 오지 않는다.)
  const isConfirmDisabled = isChannelSelectable && targetCount === 0;

  const handleConfirm = () => onConfirm(selectedKinds);

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
      minWidth={"min(640px, 92dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={
          <Flex flexDirection={"column"} gap={"2px"}>
            <Text textStyle={"body-md-bold"} color={"#444444"}>
              {t(
                isChannelSelectable
                  ? "path_pick:basket.sync_confirm.title_markets"
                  : "path_pick:basket.sync_confirm.title"
              )}
            </Text>
            <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
              {t(
                isChannelSelectable
                  ? "path_pick:basket.sync_confirm.description_markets"
                  : "path_pick:basket.sync_confirm.description"
              )}
            </Text>
          </Flex>
        }
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"12px"}>
          <Text textStyle={"body-md-regular"} color={"#1D1D1B"}>
            {t(
              isChannelSelectable
                ? "path_pick:basket.sync_confirm.body_markets"
                : "path_pick:basket.sync_confirm.body",
              { count: targetCount }
            )}
          </Text>
          {/* 이미 처리된 상품은 요청에서 자동으로 빠지므로 개수만 알린다
              (KREAM 미매칭 상품이 KREAM 파일에서 자동 제외되는 것과 같은 어법). */}
          {excludedCount > 0 && (
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t("path_pick:basket.sync_confirm.excluded_notice", {
                count: excludedCount,
              })}
            </Text>
          )}
          {/* 마켓 선택은 버튼 바로 위에 둔다. 좁은 폭에서는 카드를 세로로 쌓는다. */}
          {isChannelSelectable && (
            <Flex gap={"8px"} flexDirection={{ base: "column", md: "row" }}>
              {syncKinds.map((kind) => (
                <PathPickBasketCheckCard
                  key={kind}
                  label={t(SYNC_CHANNEL_LABEL_KEYS[kind])}
                  suffix={
                    kind === "NAVER_SYNC" && !isNaverReady
                      ? t("path_pick:basket.sync_confirm.naver_setup_needed")
                      : undefined
                  }
                  isSelected={selectedKinds.includes(kind)}
                  onToggle={() => toggleKind(kind)}
                />
              ))}
            </Flex>
          )}
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        cancelName={t("common:cancel")}
        confirmName={t("path_pick:basket.sync_confirm.confirm")}
        onCancel={onClose}
        onConfirm={handleConfirm}
        isLoading={isLoading}
        isConfirmDisabled={isConfirmDisabled}
        confirmBtnProps={{
          width: "120px",
          leftIcon: (
            <CustomIcon
              src={"download"}
              width={16}
              height={16}
              // 비활성 버튼 문구색(#C0C0C0)과 맞춘다.
              color={isConfirmDisabled ? "#C0C0C0" : "#FFFFFF"}
            />
          ),
        }}
      />
    </CustomApprovalModal>
  );
}

export default PathPickBasketSyncConfirmModal;
